import { ChevronDown, LoaderCircle, Zap } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { QuickActionPlacement } from "~/services/quickActions";
import { QuickActionIcon } from "./QuickActionIcon";
import { useInvokeQuickAction } from "./useInvokeQuickAction";
import { useQuickActions } from "./useQuickActions";

export interface QuickActionsSidebarListProps {
  placement: QuickActionPlacement;
  placementId: string;
}

/**
 * Agent/Dataset sidebar equivalent of `QuickActionsGrid`: a "Quick Actions"
 * trigger that opens a menu of cards in the Home grid's visual language
 * (icon badge, bold title, subtitle).
 */
export const QuickActionsSidebarList = ({
  placement,
  placementId,
}: QuickActionsSidebarListProps) => {
  const { actions, isLoading } = useQuickActions(placement, placementId);
  const { invoke, invokingId } = useInvokeQuickAction();

  if (isLoading || actions.length === 0) {
    return null;
  }

  return (
    <div className="px-4 pt-3">
      <DropdownMenu>
        <DropdownMenuTrigger className="group flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-primary transition-colors hover:bg-black/[0.06] dark:hover:bg-white/[0.08]">
          <Zap className="size-4" aria-hidden="true" />
          Quick Actions
          <ChevronDown
            className="ml-auto size-4 transition-transform duration-300 ease-in-out group-data-[state=open]:rotate-180"
            aria-hidden="true"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72 p-1.5">
          {actions.map((action) => (
            <DropdownMenuItem
              key={action.id}
              disabled={invokingId === action.id}
              onSelect={(event) => {
                // Radix closes the menu on select by default, before the async
                // invoke resolves — suppress that so the spinner below is
                // actually visible instead of the menu vanishing on click.
                event.preventDefault();
                invoke(action.id);
              }}
              className="items-center gap-2.5 p-2"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary dark:bg-primary/20">
                {invokingId === action.id ? (
                  <LoaderCircle className="size-5 animate-spin" aria-hidden="true" />
                ) : (
                  <QuickActionIcon name={action.icon} className="size-5 text-primary" />
                )}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-foreground">{action.title}</span>
                {action.subtitle && (
                  <span className="mt-0.5 block text-xs leading-snug whitespace-normal text-muted-foreground">
                    {action.subtitle}
                  </span>
                )}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
